import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

interface AddPropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (property: { key: string; value: any }) => void;
  title?: string;
  submitLabel?: string;
}

const AddPropertyModal: React.FC<AddPropertyModalProps> = ({ isOpen, onClose, onSave, title, submitLabel }) => {
  const [propertyKey, setPropertyKey] = useState('');
  const [valueType, setValueType] = useState<'value' | 'object' | 'array'>('value');
  const [value, setValue] = useState<any>('');
  const [objectKeys, setObjectKeys] = useState<Array<{ key: string; value: string }>>([]);
  const [arrayItems, setArrayItems] = useState<string[]>(['']);

  const convertToApiKey = (key: string) => {
    return key
      .toLowerCase()
      .replace(/[^a-z0-9\s+\-]/g, '') // Remove special characters except + and -
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim();
  };

  const handleValueTypeChange = (newType: 'value' | 'object' | 'array') => {
    setValueType(newType);
    
    switch (newType) {
      case 'value':
        setValue('');
        break;
      case 'object':
        setValue({});
        setObjectKeys([{ key: '', value: '' }]);
        break;
      case 'array':
        setValue([]);
        setArrayItems(['']);
        break;
    }
  };

  const addObjectKey = () => {
    setObjectKeys(prev => [...prev, { key: '', value: '' }]);
  };

  const updateObjectKey = (index: number, field: 'key' | 'value', newValue: string) => {
    setObjectKeys(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: newValue } : item
    ));
  };

  const removeObjectKey = (index: number) => {
    setObjectKeys(prev => prev.filter((_, i) => i !== index));
  };

  const addArrayItem = () => {
    setArrayItems(prev => [...prev, '']);
  };

  const updateArrayItem = (index: number, newValue: string) => {
    setArrayItems(prev => prev.map((item, i) => i === index ? newValue : item));
  };

  const removeArrayItem = (index: number) => {
    setArrayItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!propertyKey.trim()) {
      alert('Property key cannot be empty');
      return;
    }

    let finalValue: any;
    
    switch (valueType) {
      case 'value':
        finalValue = value;
        break;
      case 'object':
        // Convert object keys to actual object
        const obj: Record<string, string> = {};
        objectKeys.forEach(item => {
          if (item.key.trim()) {
            const convertedKey = convertToApiKey(item.key);
            obj[convertedKey] = item.value;
          }
        });
        finalValue = obj;
        break;
      case 'array':
        // Filter out empty items
        finalValue = arrayItems.filter(item => item.trim() !== '');
        break;
    }

    const convertedKey = convertToApiKey(propertyKey);
    console.log('Original key:', propertyKey);
    console.log('Converted key:', convertedKey);
    
    onSave({
      key: convertedKey,
      value: finalValue
    });

    // Reset form
    setPropertyKey('');
    setValueType('value');
    setValue('');
    setObjectKeys([]);
    setArrayItems(['']);
    onClose();
  };

  const handleClose = () => {
    // Reset form
    setPropertyKey('');
    setValueType('value');
    setValue('');
    setObjectKeys([]);
    setArrayItems(['']);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{title || 'Add New Property'}</h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Property Key and Value Type in same row */}
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Property Key *
              </label>
              <input
                type="text"
                value={propertyKey}
                onChange={(e) => setPropertyKey(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter property key"
              />
            </div>
            <div className="w-40">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Value Type
              </label>
              <div className="relative">
                <select
                  value={valueType}
                  onChange={(e) => handleValueTypeChange(e.target.value as 'value' | 'object' | 'array')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-100 appearance-none bg-white"
                >
                  <option value="value">Value</option>
                  <option value="object">Object</option>
                  <option value="array">Array</option>
                </select>
                {/* Custom dropdown arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Value Input based on type */}
          {valueType === 'value' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Value
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter value"
              />
            </div>
          )}

          {valueType === 'object' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-slate-700">
                  Object Properties
                </label>
                <button
                  onClick={addObjectKey}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Key
                </button>
              </div>
              <div className="space-y-2">
                {objectKeys.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={item.key}
                      onChange={(e) => updateObjectKey(index, 'key', e.target.value)}
                      className="w-1/3 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Key"
                    />
                    <input
                      type="text"
                      value={item.value}
                      onChange={(e) => updateObjectKey(index, 'value', e.target.value)}
                      className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Value"
                    />
                    <button
                      onClick={() => removeObjectKey(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove property"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {valueType === 'array' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-slate-700">
                  Array Items
                </label>
                <button
                  onClick={addArrayItem}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
              <div className="space-y-2">
                {arrayItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => updateArrayItem(index, e.target.value)}
                      className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Array item"
                    />
                    <button
                      onClick={() => removeArrayItem(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {submitLabel || 'Add Property'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyModal;
